/**
 * ProspeccionPanel.jsx — IMPL-043 (2026-05-19)
 *
 * Panel del módulo Prospección. Se renderiza desde AdminPanel cuando tab === 'prospeccion'.
 * 3 sub-tabs: Oportunidades · Configuración · Logs.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Compass, Play, RefreshCw, Mail, Linkedin, Phone, Copy, ChevronDown, ChevronUp,
  Sparkles, Settings, History, X, Plus, AlertCircle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem("mobilsafe_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const URGENCIA_COLOR = {
  ALTA: "bg-red-500/20 text-red-700 border-red-500/40",
  MEDIA: "bg-amber-500/20 text-amber-700 border-amber-500/40",
  BAJA: "bg-emerald-500/20 text-emerald-700 border-emerald-500/40",
};

const STATUS_COLOR = {
  pendiente: "bg-blue-500/20 text-blue-700",
  contactado: "bg-emerald-500/20 text-emerald-700",
  descartado: "bg-slate-500/20 text-slate-500",
};

export default function ProspeccionPanel({ mode }) {
  // mode === 'oportunidades' → solo tabla oportunidades, sin sub-tabs
  // mode === 'keywords' | 'horario' | 'modelos' | 'logs' → sección concreta, sin sub-tabs
  // mode === undefined → modo completo (oportunidades + configuración + logs)
  const [subTab, setSubTab] = useState("oportunidades");

  if (mode === "oportunidades") return <Oportunidades />;
  if (mode === "keywords") return <ConfigSection section="keywords" />;
  if (mode === "horario") return <ConfigSection section="horario" />;
  if (mode === "modelos") return <ConfigSection section="modelos" />;
  if (mode === "logs") return <Logs />;

  return (
    <div className="space-y-4" data-testid="prospeccion-panel">
      {/* Cabecera */}
      <div className="bg-gradient-to-r from-violet-500/10 to-purple-500/10 border border-violet-500/30 rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-violet-500/20 rounded-lg">
            <Compass className="w-5 h-5 text-violet-700" />
          </div>
          <div>
            <h2 className="text-lg font-bold">Prospección automatizada</h2>
            <p className="text-xs text-slate-500">Pipeline diario de inteligencia comercial · Groq + RSS</p>
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-3">
        <SubTab id="oportunidades" current={subTab} onClick={setSubTab} icon={<Sparkles className="w-4 h-4" />}>
          Oportunidades
        </SubTab>
        <SubTab id="configuracion" current={subTab} onClick={setSubTab} icon={<Settings className="w-4 h-4" />}>
          Configuración
        </SubTab>
        <SubTab id="logs" current={subTab} onClick={setSubTab} icon={<History className="w-4 h-4" />}>
          Logs
        </SubTab>
      </div>

      {subTab === "oportunidades" && <Oportunidades />}
      {subTab === "configuracion" && <Configuracion />}
      {subTab === "logs" && <Logs />}
    </div>
  );
}

function SubTab({ id, current, onClick, icon, children }) {
  const active = current === id;
  return (
    <button
      onClick={() => onClick(id)}
      data-testid={`prospeccion-subtab-${id}`}
      className={`px-4 py-2 rounded-lg font-semibold flex items-center gap-2 transition-all ${
        active ? "bg-violet-500 text-white" : "bg-violet-500/10 text-violet-700 hover:bg-violet-500/20"
      }`}
    >
      {icon} {children}
    </button>
  );
}

// ============== SUB-TAB 1: OPORTUNIDADES ==============

function Oportunidades() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const url = filter ? `${API}/prospecting/opportunities?status=${filter}` : `${API}/prospecting/opportunities`;
      const r = await axios.get(url, { headers: authHeaders() });
      setItems(r.data.opportunities || []);
    } catch (e) {
      toast.error("Error cargando oportunidades");
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const runPipeline = async () => {
    if (!window.confirm("¿Ejecutar el pipeline ahora? Consumirá tokens de Groq.")) return;
    setRunning(true);
    try {
      const r = await axios.post(`${API}/prospecting/run`, {}, { headers: authHeaders() });
      toast.success(`Pipeline OK · ${r.data.opportunities || 0} oportunidades detectadas`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error ejecutando pipeline");
    }
    setRunning(false);
  };

  const changeStatus = async (oppId, newStatus) => {
    try {
      await axios.put(
        `${API}/prospecting/opportunities/${oppId}/status`,
        { status: newStatus },
        { headers: authHeaders() }
      );
      toast.success(`Estado: ${newStatus}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error actualizando estado");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="prospeccion-filter-status"
            className="px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg text-sm"
          >
            <option value="">Todos los estados</option>
            <option value="pendiente">Pendientes</option>
            <option value="contactado">Contactados</option>
            <option value="descartado">Descartados</option>
          </select>
          <span className="text-xs text-slate-500">{items.length} oportunidades</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            data-testid="prospeccion-reload-btn"
            className="px-3 py-2 bg-slate-200 hover:bg-slate-600 rounded-lg text-sm font-medium flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" /> Recargar
          </button>
          <button
            onClick={runPipeline}
            disabled={running}
            data-testid="prospeccion-run-btn"
            className="px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 rounded-lg text-sm font-bold flex items-center gap-2"
          >
            {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Ejecutar ahora
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500"><RefreshCw className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-slate-200 rounded-xl">
          <Sparkles className="w-10 h-10 mx-auto mb-3 text-slate-500" />
          <p className="text-slate-500 font-medium">No hay oportunidades aún</p>
          <p className="text-xs text-slate-500 mt-1">Ejecuta el pipeline manualmente o espera al cron de las 06:00</p>
        </div>
      ) : (
        <div className="bg-white shadow-sm border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Fecha</th>
                <th className="text-left px-4 py-3">Empresa</th>
                <th className="text-left px-4 py-3">Noticia</th>
                <th className="text-left px-4 py-3">Urgencia</th>
                <th className="text-left px-4 py-3">Mensajes</th>
                <th className="text-left px-4 py-3">Estado</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((opp, idx) => (
                <React.Fragment key={opp.id || idx}>
                  <tr
                    className="border-t border-slate-200 hover:bg-slate-100 cursor-pointer"
                    onClick={() => setExpanded(expanded === idx ? null : idx)}
                    data-testid={`prospeccion-row-${idx}`}
                  >
                    <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                      {(opp.processed_at || "").slice(0, 10)}
                    </td>
                    <td className="px-4 py-3 font-semibold">{opp.empresa || "—"}</td>
                    <td className="px-4 py-3 max-w-md truncate">{opp.titulo || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2 py-1 rounded border ${URGENCIA_COLOR[opp.urgencia] || URGENCIA_COLOR.MEDIA}`}>
                        {opp.urgencia || "MEDIA"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {opp.messages ? (
                        <div className="flex gap-1 text-emerald-400">
                          <Linkedin className="w-4 h-4" />
                          <Mail className="w-4 h-4" />
                          <Phone className="w-4 h-4" />
                        </div>
                      ) : (
                        <span className="text-xs text-slate-500">Sin generar</span>
                      )}
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={opp.status || "pendiente"}
                        onChange={(e) => changeStatus(opp.id, e.target.value)}
                        data-testid={`prospeccion-status-${idx}`}
                        className={`px-2 py-1 rounded text-xs font-bold ${STATUS_COLOR[opp.status || "pendiente"]}`}
                      >
                        <option value="pendiente">Pendiente</option>
                        <option value="contactado">Contactado</option>
                        <option value="descartado">Descartado</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {expanded === idx ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </td>
                  </tr>
                  {expanded === idx && (
                    <tr className="bg-slate-50">
                      <td colSpan={7} className="px-4 py-4">
                        <OpportunityDetail opp={opp} onReload={load} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OpportunityDetail({ opp, onReload }) {
  const [generating, setGenerating] = useState(false);
  const [messages, setMessages] = useState(opp.messages || null);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado`);
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/prospecting/generate-messages/${opp.id}`, {}, { headers: authHeaders() });
      setMessages(r.data.messages);
      toast.success("Mensajes generados");
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando mensajes");
    }
    setGenerating(false);
  };

  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <h4 className="text-xs text-slate-500 uppercase font-bold mb-2">Noticia completa</h4>
          <p className="text-sm text-slate-800">{opp.titulo}</p>
          {opp.problema && <p className="text-xs text-slate-500 mt-2"><strong>Problema:</strong> {opp.problema}</p>}
          {opp.link && (
            <a href={opp.link} target="_blank" rel="noopener noreferrer" className="text-xs text-violet-400 hover:underline mt-2 inline-block">
              Ver fuente →
            </a>
          )}
        </div>
        <div>
          <h4 className="text-xs text-slate-500 uppercase font-bold mb-2">Datos</h4>
          <ul className="text-xs text-slate-700 space-y-1">
            <li><strong>Flota estimada:</strong> ~{opp.flota_estimada || "?"} vehículos</li>
            <li><strong>Impacto:</strong> ~{opp.mwh_potenciales || "?"} MWh</li>
            <li><strong>Plazo acción:</strong> {opp.plazo_accion || "—"}</li>
            <li><strong>Tipo:</strong> {opp.tipo_trigger || "—"}</li>
          </ul>
        </div>
      </div>

      {!messages ? (
        <button
          onClick={generate}
          disabled={generating}
          data-testid={`prospeccion-generate-${opp.id}`}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg text-sm font-bold flex items-center gap-2"
        >
          {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Generar mensajes con IA
        </button>
      ) : (
        <div className="grid md:grid-cols-3 gap-3">
          <MessageCard icon={<Linkedin className="w-4 h-4" />} title="LinkedIn" text={messages.linkedin} onCopy={() => copy(messages.linkedin, "LinkedIn")} />
          <MessageCard icon={<Mail className="w-4 h-4" />} title="Email" text={messages.email} onCopy={() => copy(messages.email, "Email")} />
          <MessageCard icon={<Phone className="w-4 h-4" />} title="Llamada" text={messages.llamada} onCopy={() => copy(messages.llamada, "Llamada")} />
        </div>
      )}
    </div>
  );
}

function MessageCard({ icon, title, text, onCopy }) {
  return (
    <div className="bg-slate-100 border border-slate-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-violet-700 flex items-center gap-1">{icon} {title}</span>
        <button onClick={onCopy} className="text-slate-500 hover:text-white"><Copy className="w-3.5 h-3.5" /></button>
      </div>
      <p className="text-xs text-slate-800 whitespace-pre-wrap">{text}</p>
    </div>
  );
}

// ============== SUB-TAB 2: CONFIGURACIÓN ==============

/**
 * ConfigSection — vista parcial de configuración (una sección concreta).
 * Usado desde AdminPanel cuando el usuario pulsa un tab de "Prospección config".
 *   section = 'keywords' | 'horario' | 'modelos'
 */
function ConfigSection({ section }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/prospecting/config`, { headers: authHeaders() });
        setConfig(r.data);
      } catch {
        toast.error("Error cargando configuración");
      }
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/prospecting/config`, config, { headers: authHeaders() });
      toast.success("Configuración guardada");
    } catch {
      toast.error("Error guardando configuración");
    }
    setSaving(false);
  };

  if (loading || !config) return <div className="text-center py-12 text-slate-500"><RefreshCw className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6" data-testid={`prospeccion-config-${section}`}>
      {section === "keywords" && <KeywordsTargetSections config={config} setConfig={setConfig} />}
      {section === "horario" && <HorarioSection config={config} setConfig={setConfig} />}
      {section === "modelos" && <ModelosIASection config={config} setConfig={setConfig} />}
      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          data-testid="prospeccion-save-btn"
          className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 rounded-lg text-sm font-bold"
        >
          {saving ? "Guardando…" : "Guardar configuración"}
        </button>
      </div>
    </div>
  );
}

function Configuracion() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/prospecting/config`, { headers: authHeaders() });
        setConfig(r.data);
      } catch (e) {
        toast.error("Error cargando configuración");
      }
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/prospecting/config`, config, { headers: authHeaders() });
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error("Error guardando configuración");
    }
    setSaving(false);
  };

  if (loading || !config) return <div className="text-center py-12 text-slate-500"><RefreshCw className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6">
      <KeywordsTargetSections config={config} setConfig={setConfig} />
      <ModelosIASection config={config} setConfig={setConfig} />
      <HorarioSection config={config} setConfig={setConfig} />

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          data-testid="prospeccion-save-btn"
          className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 rounded-lg text-sm font-bold"
        >
          {saving ? "Guardando…" : "Guardar configuración"}
        </button>
      </div>
    </div>
  );
}

// === Sub-componentes reutilizables ===

function KeywordsTargetSections({ config, setConfig }) {
  return (
    <>
      <Section title="Keywords">
        {["must_include", "trigger_directo", "dolor", "obligacion", "oportunidad", "excluir"].map((cat) => (
          <ChipEditor
            key={cat}
            label={cat.replace(/_/g, " ").toUpperCase()}
            items={config.keywords?.[cat] || []}
            onChange={(items) => setConfig({ ...config, keywords: { ...config.keywords, [cat]: items } })}
            danger={cat === "excluir"}
          />
        ))}
      </Section>
      <Section title="Target">
        <ChipEditor
          label="Segmentos"
          items={config.target_companies?.segments || []}
          onChange={(items) => setConfig({ ...config, target_companies: { ...config.target_companies, segments: items } })}
        />
        <div>
          <label className="text-xs text-slate-500 uppercase font-bold block mb-1">Tamaño mínimo de flota</label>
          <input
            type="number"
            value={config.target_companies?.min_fleet_size || 0}
            onChange={(e) => setConfig({ ...config, target_companies: { ...config.target_companies, min_fleet_size: parseInt(e.target.value || 0) } })}
            data-testid="prospeccion-min-fleet"
            className="px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm w-32"
          />
        </div>
        <ChipEditor
          label="Decision Makers"
          items={config.target_companies?.decision_makers || []}
          onChange={(items) => setConfig({ ...config, target_companies: { ...config.target_companies, decision_makers: items } })}
        />
      </Section>
    </>
  );
}

function ModelosIASection({ config, setConfig }) {
  return (
    <Section title="Modelos IA">
      <p className="text-xs text-amber-700 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-center gap-2">
        <AlertCircle className="w-4 h-4" />
        Agente 2 con Claude genera mensajes de mayor calidad comercial (próximamente).
      </p>
      {["agent_1a", "agent_1b", "agent_2"].map((agent) => (
        <div key={agent} className="flex items-center justify-between gap-3">
          <span className="text-sm font-medium uppercase">{agent.replace("_", " ")}</span>
          <select
            value={config.llm_config?.[agent] || "groq"}
            onChange={(e) => setConfig({ ...config, llm_config: { ...config.llm_config, [agent]: e.target.value } })}
            data-testid={`prospeccion-llm-${agent}`}
            className="px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm"
          >
            <option value="groq">Groq Llama 3.3</option>
            <option value="claude" disabled>Claude Sonnet (próximamente)</option>
          </select>
        </div>
      ))}
    </Section>
  );
}

function HorarioSection({ config, setConfig }) {
  return (
    <Section title="Horario">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-slate-500 uppercase font-bold block mb-1">Hora pipeline</label>
          <input
            type="time"
            value={config.schedule?.cron_time || "06:00"}
            onChange={(e) => setConfig({ ...config, schedule: { ...config.schedule, cron_time: e.target.value } })}
            data-testid="prospeccion-cron-time"
            className="px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-slate-500 uppercase font-bold block mb-1">Hora email resumen</label>
          <input
            type="time"
            value={config.schedule?.email_summary_time || "08:00"}
            onChange={(e) => setConfig({ ...config, schedule: { ...config.schedule, email_summary_time: e.target.value } })}
            data-testid="prospeccion-email-time"
            className="px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm"
          />
        </div>
      </div>
      <div>
        <label className="text-xs text-slate-500 uppercase font-bold block mb-1">Email destinatario</label>
        <input
          type="email"
          value={config.schedule?.email_to || ""}
          onChange={(e) => setConfig({ ...config, schedule: { ...config.schedule, email_to: e.target.value } })}
          data-testid="prospeccion-email-to"
          className="px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm w-full"
        />
      </div>
      <div>
        <label className="text-xs text-slate-500 uppercase font-bold block mb-2">Días activos</label>
        <div className="flex gap-2">
          {["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map((d) => {
            const active = (config.schedule?.days || []).includes(d);
            return (
              <button
                key={d}
                onClick={() => {
                  const days = active ? config.schedule.days.filter((x) => x !== d) : [...(config.schedule.days || []), d];
                  setConfig({ ...config, schedule: { ...config.schedule, days } });
                }}
                data-testid={`prospeccion-day-${d}`}
                className={`w-12 h-10 rounded-lg text-xs font-bold uppercase transition ${
                  active ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}
              >
                {d.slice(0, 2)}
              </button>
            );
          })}
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={config.enabled !== false}
          onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
          data-testid="prospeccion-enabled"
          className="w-4 h-4 accent-violet-600"
        />
        Módulo activo (el cron se ejecuta automáticamente)
      </label>
    </Section>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white shadow-sm border border-slate-200 rounded-xl p-5 space-y-4">
      <h3 className="text-sm font-bold text-violet-700 uppercase tracking-wider">{title}</h3>
      {children}
    </div>
  );
}

function ChipEditor({ label, items, onChange, danger = false }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (v && !items.includes(v)) {
      onChange([...items, v]);
      setInput("");
    }
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div>
      <label className="text-xs text-slate-500 uppercase font-bold block mb-1">{label}</label>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {items.map((t, i) => (
          <span
            key={i}
            onClick={() => remove(i)}
            data-testid={`chip-${label.toLowerCase()}-${i}`}
            className={`px-2 py-1 rounded-full text-xs cursor-pointer flex items-center gap-1 ${
              danger ? "bg-red-500/20 text-red-700 hover:bg-red-500/40" : "bg-violet-500/20 text-violet-700 hover:bg-violet-500/40"
            }`}
          >
            {t} <X className="w-3 h-3" />
          </span>
        ))}
      </div>
      <div className="flex gap-1">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder="Añadir…"
          className="flex-1 px-3 py-1.5 bg-slate-100 border border-slate-200 rounded-lg text-sm"
        />
        <button onClick={add} className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 rounded-lg text-sm">
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ============== SUB-TAB 3: LOGS ==============

function Logs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/prospecting/logs`, { headers: authHeaders() });
        setLogs(r.data.logs || []);
      } catch {
        toast.error("Error cargando logs");
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="text-center py-12 text-slate-500"><RefreshCw className="w-6 h-6 animate-spin mx-auto" /></div>;

  if (logs.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed border-slate-200 rounded-xl">
        <History className="w-10 h-10 mx-auto mb-3 text-slate-500" />
        <p className="text-slate-500 font-medium">Sin ejecuciones aún</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-sm border border-slate-200 rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-100 text-slate-500 text-xs uppercase">
          <tr>
            <th className="text-left px-4 py-3">Fecha</th>
            <th className="text-right px-4 py-3">Noticias</th>
            <th className="text-right px-4 py-3">Relevantes</th>
            <th className="text-right px-4 py-3">Oportunidades</th>
            <th className="text-left px-4 py-3">Estado</th>
            <th className="text-left px-4 py-3">Errores</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log, idx) => (
            <tr key={idx} className="border-t border-slate-200" data-testid={`prospeccion-log-${idx}`}>
              <td className="px-4 py-3 text-xs text-slate-500">{(log.timestamp || "").slice(0, 19).replace("T", " ")}</td>
              <td className="px-4 py-3 text-right">{log.module_0?.filtered ?? 0}</td>
              <td className="px-4 py-3 text-right">{log.agent_1a?.relevant ?? 0}</td>
              <td className="px-4 py-3 text-right font-bold">{log.opportunities ?? log.agent_1b?.opportunities ?? 0}</td>
              <td className="px-4 py-3">
                <span className={`text-xs px-2 py-1 rounded ${log.status === "ok" ? "bg-emerald-500/20 text-emerald-700" : "bg-red-500/20 text-red-700"}`}>
                  {log.status || "ok"}
                </span>
              </td>
              <td className="px-4 py-3 text-xs text-red-700">
                {(log.errors || []).length > 0 ? log.errors.join("; ").slice(0, 80) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
